import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, getErrorMessage } from '@/lib/api'
import { toast } from '@/lib/toast'
import { useAuthStore } from '@/stores/authStore'
import type {
  TokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  Verify2FARequest,
  ActivateAccountRequest,
  ValidateActivationTokenRequest,
  ValidateActivationTokenResponse,
  MessageResponse,
} from '@/types/auth'

/**
 * Hook for authentication mutations (login, logout, password reset, etc.)
 */
export function useAuth() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    setTokens,
    set2FARequired,
    logout: clearAuth,
    setLoading,
  } = useAuthStore()

  /**
   * Login mutation
   * Handles both regular login and 2FA-required responses
   */
  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      // OAuth2 password flow uses form-urlencoded data
      const formData = new URLSearchParams()
      formData.append('username', credentials.email)
      formData.append('password', credentials.password)

      const response = await api.post<TokenResponse>('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      if (data.requires_2fa) {
        // Store temporary token for 2FA verification
        set2FARequired(true, data.access_token)
        navigate('/2fa')
      } else {
        // Full login - store token and redirect
        setTokens(data.access_token)
        
        // Check for intended destination
        const redirectPath = sessionStorage.getItem('redirect_after_login')
        if (redirectPath) {
          sessionStorage.removeItem('redirect_after_login')
          navigate(redirectPath)
        } else {
          navigate('/dashboard')
        }
      }
    },
    onError: (error) => {
      setLoading(false)
      const message = getErrorMessage(error)
      toast.error('Login failed', { description: message })
    },
  })

  /**
   * Verify 2FA code mutation
   */
  const verify2FAMutation = useMutation({
    mutationFn: async (data: Verify2FARequest) => {
      const { tempToken } = useAuthStore.getState()
      
      const response = await api.post<TokenResponse>('/verify-2fa', data, {
        headers: {
          Authorization: `Bearer ${tempToken}`,
        },
      })
      return response.data
    },
    onSuccess: (data) => {
      setTokens(data.access_token)
      toast.success('Welcome back!')
      
      // Check for intended destination
      const redirectPath = sessionStorage.getItem('redirect_after_login')
      if (redirectPath) {
        sessionStorage.removeItem('redirect_after_login')
        navigate(redirectPath)
      } else {
        navigate('/dashboard')
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Verification failed', { description: message })
    },
  })

  /**
   * Resend 2FA code mutation
   */
  const resend2FAMutation = useMutation({
    mutationFn: async () => {
      const { tempToken } = useAuthStore.getState()
      
      const response = await api.post<MessageResponse>('/resend-2fa-code', null, {
        headers: {
          Authorization: `Bearer ${tempToken}`,
        },
      })
      return response.data
    },
    onSuccess: () => {
      toast.success('Code sent', { description: 'A new verification code has been sent to your phone.' })
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Failed to resend code', { description: message })
    },
  })

  /**
   * Forgot password mutation
   */
  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordRequest) => {
      const response = await api.post<string>('/forgot-password', data)
      return response.data
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Request failed', { description: message })
    },
  })

  /**
   * Reset password mutation
   */
  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordRequest) => {
      const response = await api.post<string>('/reset-password', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Password updated', { description: 'You can now sign in with your new password.' })
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Password reset failed', { description: message })
    },
  })

  /**
   * Validate activation token mutation
   */
  const validateActivationTokenMutation = useMutation({
    mutationFn: async (data: ValidateActivationTokenRequest) => {
      const response = await api.post<ValidateActivationTokenResponse>(
        '/validate-activation-token',
        data
      )
      return response.data
    },
  })

  /**
   * Activate account mutation
   */
  const activateAccountMutation = useMutation({
    mutationFn: async (data: ActivateAccountRequest) => {
      const response = await api.post<MessageResponse>('/activate-account', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Account activated', { description: 'You can now sign in to your account.' })
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Activation failed', { description: message })
    },
  })

  /**
   * Logout function
   */
  const logout = () => {
    clearAuth()
    queryClient.clear()
    toast.info('Signed out', { description: 'You have been signed out successfully.' })
    navigate('/login')
  }

  return {
    // Login
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,

    // 2FA
    verify2FA: verify2FAMutation.mutate,
    verify2FAAsync: verify2FAMutation.mutateAsync,
    isVerifying2FA: verify2FAMutation.isPending,
    verify2FAError: verify2FAMutation.error,

    // Resend 2FA
    resend2FA: resend2FAMutation.mutate,
    isResending2FA: resend2FAMutation.isPending,
    resend2FAError: resend2FAMutation.error,

    // Forgot password
    forgotPassword: forgotPasswordMutation.mutate,
    forgotPasswordAsync: forgotPasswordMutation.mutateAsync,
    isSendingResetEmail: forgotPasswordMutation.isPending,
    forgotPasswordError: forgotPasswordMutation.error,
    forgotPasswordSuccess: forgotPasswordMutation.isSuccess,

    // Reset password
    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
    isResettingPassword: resetPasswordMutation.isPending,
    resetPasswordError: resetPasswordMutation.error,

    // Validate activation token
    validateActivationToken: validateActivationTokenMutation.mutate,
    validateActivationTokenAsync: validateActivationTokenMutation.mutateAsync,
    isValidatingToken: validateActivationTokenMutation.isPending,
    validateTokenError: validateActivationTokenMutation.error,
    validateTokenSuccess: validateActivationTokenMutation.isSuccess,

    // Activate account
    activateAccount: activateAccountMutation.mutate,
    activateAccountAsync: activateAccountMutation.mutateAsync,
    isActivatingAccount: activateAccountMutation.isPending,
    activateAccountError: activateAccountMutation.error,
    activateAccountSuccess: activateAccountMutation.isSuccess,

    // Logout
    logout,
  }
}

export default useAuth
